import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import FeatureSection from './components/FeatureSection';
import DashboardShell from './components/DashboardShell';
import Navigation from '../components/Navigation';
import Footer from '../../Landing/footer';
import ComprehensiveFAQ from '../components/ComprehensiveFAQ';
import CtaSection from '../components/LifetimeCTA';
import { ThemeProvider } from '../../Landing/theme-provider';
import { 
  Layers, 
  FileText, 
  MessageCircle, 
  Palette, 
  BarChart3, 
  History, 
  UserPlus, 
  ShoppingCart 
} from 'lucide-react';

// Import visual components for mobile display
import ChatWidgetVisual from './components/visuals/ChatWidgetVisual';
import LeadGenVisual from './components/visuals/LeadGenVisual';
import TranscriptsVisual from './components/visuals/TranscriptsVisual';
import AnalyticsVisual from './components/visuals/AnalyticsVisual';
import CustomizationVisual from './components/visuals/CustomizationVisual';
import MultiBrandVisual from './components/visuals/MultiBrandVisual';
import FilesVisual from './components/visuals/FilesVisual';
import WooCommerceVisual from './components/visuals/WooCommerceVisual';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const Header = styled.div`
  padding: 80px 24px 60px;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
  
  h1 {
    font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 3.5rem;
    font-weight: 700;
    color: #0b0f1a;
    margin-bottom: 24px;
    letter-spacing: -0.04em;
    line-height: 1.1;

    @media (min-width: 1024px) {
      font-size: 4.5rem;
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }

    @media (max-width: 480px) {
      font-size: 2rem;
      margin-bottom: 16px;
    }
  }
  
  p {
    font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 1.25rem;
    color: #4b5563;
    line-height: 1.6;
    max-width: 700px;
    margin: 0 auto;

    @media (max-width: 480px) {
      font-size: 0.95rem;
      line-height: 1.5;
    }
  }

  @media (max-width: 768px) {
    padding: 60px 16px 40px;
  }

  @media (max-width: 480px) {
    padding: 40px 12px 32px;
  }
`;

const SplitLayout = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px 100px;
  position: relative;
  
  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 60px;
  }

  @media (max-width: 768px) {
    padding: 0 16px 60px;
  }

  @media (max-width: 480px) {
    padding: 0 12px 40px;
  }
`;

const TextColumn = styled.div`
  flex: 1;
  padding-top: 40px;
  
  @media (min-width: 1024px) {
    max-width: 350px;
    padding-top: 30vh; // Start content lower to allow dashboard to settle
  }

  @media (max-width: 480px) {
    padding-top: 24px;
  }
`;

const StickyColumn = styled.div`
  flex: 2.5;
  position: sticky;
  top: 100px;
  height: calc(100vh - 140px); // Viewport minus margins
  display: none;
  
  @media (min-width: 1024px) {
    display: block;
  }
`;

// Mobile visual container - shows actual dashboard visuals scaled down
const MobileVisualContainer = styled.div`
  display: block;
  margin: 20px 0 40px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  background: #f9fafb;
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const MobileVisualWrapper = styled.div`
  width: 100%;
  height: 280px;
  position: relative;
  overflow: hidden;
  
  /* Scale down the dashboard visual content */
  & > div {
    transform: scale(0.65);
    transform-origin: top center;
    width: 154%;
    height: 154%;
    margin-left: -27%;
  }

  @media (max-width: 480px) {
    height: 240px;
    
    & > div {
      transform: scale(0.55);
      width: 182%;
      height: 182%;
      margin-left: -41%;
    }
  }

  @media (max-width: 380px) {
    height: 200px;
    
    & > div {
      transform: scale(0.45);
      width: 222%;
      height: 222%;
      margin-left: -61%;
    }
  }
`;

const MobileVisualLabel = styled.div`
  padding: 12px 16px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const MobileVisualIconSmall = styled.div<{ featureId?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch(props.featureId) {
      case 'multi-brand': return 'linear-gradient(135deg, #3b82f6, #1d4ed8)';
      case 'files': return 'linear-gradient(135deg, #a855f7, #7c3aed)';
      case 'chat-widget': return 'linear-gradient(135deg, #22c55e, #16a34a)';
      case 'customization': return 'linear-gradient(135deg, #f97316, #ea580c)';
      case 'analytics': return 'linear-gradient(135deg, #ec4899, #db2777)';
      case 'transcripts': return 'linear-gradient(135deg, #8b5cf6, #7c3aed)';
      case 'lead-gen': return 'linear-gradient(135deg, #06b6d4, #0891b2)';
      case 'woocommerce': return 'linear-gradient(135deg, #eab308, #ca8a04)';
      default: return 'linear-gradient(135deg, #6366f1, #4f46e5)';
    }
  }};
  color: white;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FeaturesPage: React.FC = () => {
    const [activeFeature, setActiveFeature] = useState('multi-brand');
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            const sections = document.querySelectorAll('section[id]');
            let current = '';

            sections.forEach((section) => {
                const rect = section.getBoundingClientRect();
                // Trigger when the section crosses the middle of the viewport
                if (rect.top <= window.innerHeight / 2) {
                    current = section.getAttribute('id') || '';
                }
            });

            if (current) {
                setActiveFeature(current);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            setActiveFeature(id);
            const elementPosition = element.getBoundingClientRect().top;
            const viewportCenter = window.innerHeight / 2;
            const elementCenter = element.offsetHeight / 2;
            const offsetPosition = elementPosition + window.pageYOffset - (viewportCenter - elementCenter);

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            setTimeout(() => {
                scrollToSection(id);
            }, 100);
        }
    }, [location.hash]);

    return (
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            <PageContainer>
                <Navigation />
                <Header>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        Everything you need to support your customers
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        Powerful features designed to help you engage, support, and convert visitors into customers.
                    </motion.p>
                </Header>

                <SplitLayout>
                    <TextColumn>
                        <FeatureSection
                            id="multi-brand"
                            title="Multi-brand Support"
                            description="Manage multiple bots from one dashboard."
                            benefits={['Unified view', 'Separate knowledge bases']}
                            visual={null}
                            isActive={activeFeature === 'multi-brand'}
                        />
                        <MobileVisualContainer>
                            <MobileVisualWrapper>
                                <MultiBrandVisual />
                            </MobileVisualWrapper>
                            <MobileVisualLabel>
                                <MobileVisualIconSmall featureId="multi-brand">
                                    <Layers size={18} />
                                </MobileVisualIconSmall>
                                Unified dashboard for all your chatbots
                            </MobileVisualLabel>
                        </MobileVisualContainer>

                        <FeatureSection
                            id="files"
                            title="Custom Knowledge Base"
                            description="Train your bot with your own documents."
                            benefits={['Drag & drop upload', 'Auto-indexing']}
                            visual={null}
                            isActive={activeFeature === 'files'}
                        />
                        <MobileVisualContainer>
                            <MobileVisualWrapper>
                                <FilesVisual />
                            </MobileVisualWrapper>
                            <MobileVisualLabel>
                                <MobileVisualIconSmall featureId="files">
                                    <FileText size={18} />
                                </MobileVisualIconSmall>
                                Upload PDFs, docs, and web pages
                            </MobileVisualLabel>
                        </MobileVisualContainer>

                        <FeatureSection
                            id="chat-widget"
                            title="Embeddable Chat Widget"
                            description="Smart, data-trained chatbot that automates support."
                            benefits={['Customizable UI', 'Seamless integration']}
                            visual={null}
                            isActive={activeFeature === 'chat-widget'}
                        />
                        <MobileVisualContainer>
                            <MobileVisualWrapper>
                                <ChatWidgetVisual />
                            </MobileVisualWrapper>
                            <MobileVisualLabel>
                                <MobileVisualIconSmall featureId="chat-widget">
                                    <MessageCircle size={18} />
                                </MobileVisualIconSmall>
                                One script tag to add anywhere
                            </MobileVisualLabel>
                        </MobileVisualContainer>

                        <FeatureSection
                            id="customization"
                            title="Full Customization"
                            description="Match the chatbot to your brand identity."
                            benefits={['Theme builder', 'White labeling']}
                            visual={null}
                            isActive={activeFeature === 'customization'}
                        />
                        <MobileVisualContainer>
                            <MobileVisualWrapper>
                                <CustomizationVisual />
                            </MobileVisualWrapper>
                            <MobileVisualLabel>
                                <MobileVisualIconSmall featureId="customization">
                                    <Palette size={18} />
                                </MobileVisualIconSmall>
                                Match colors, fonts, and style to your brand
                            </MobileVisualLabel>
                        </MobileVisualContainer>

                        <FeatureSection
                            id="analytics"
                            title="Advanced Analytics"
                            description="Track trends, topics, and user satisfaction."
                            benefits={['Topic analysis', 'Geo heatmaps']}
                            visual={null}
                            isActive={activeFeature === 'analytics'}
                        />
                        <MobileVisualContainer>
                            <MobileVisualWrapper>
                                <AnalyticsVisual />
                            </MobileVisualWrapper>
                            <MobileVisualLabel>
                                <MobileVisualIconSmall featureId="analytics">
                                    <BarChart3 size={18} />
                                </MobileVisualIconSmall>
                                Insights on conversations and engagement
                            </MobileVisualLabel>
                        </MobileVisualContainer>

                        <FeatureSection
                            id="transcripts"
                            title="Session Transcripts"
                            description="Review full conversation history and metadata."
                            benefits={['Searchable archives', 'User insights']}
                            visual={null}
                            isActive={activeFeature === 'transcripts'}
                        />
                        <MobileVisualContainer>
                            <MobileVisualWrapper>
                                <TranscriptsVisual />
                            </MobileVisualWrapper>
                            <MobileVisualLabel>
                                <MobileVisualIconSmall featureId="transcripts">
                                    <History size={18} />
                                </MobileVisualIconSmall>
                                Full searchable conversation archives
                            </MobileVisualLabel>
                        </MobileVisualContainer>

                        <FeatureSection
                            id="lead-gen"
                            title="Lead Generation"
                            description="Capture leads 24/7 with in-chat forms."
                            benefits={['Custom fields', 'CRM export']}
                            visual={null}
                            isActive={activeFeature === 'lead-gen'}
                        />
                        <MobileVisualContainer>
                            <MobileVisualWrapper>
                                <LeadGenVisual />
                            </MobileVisualWrapper>
                            <MobileVisualLabel>
                                <MobileVisualIconSmall featureId="lead-gen">
                                    <UserPlus size={18} />
                                </MobileVisualIconSmall>
                                Capture contact info automatically
                            </MobileVisualLabel>
                        </MobileVisualContainer>

                        <FeatureSection
                            id="woocommerce"
                            title="WooCommerce Integration"
                            description="Connect your store for instant order updates."
                            benefits={['Order tracking', 'Product search']}
                            visual={null}
                            isActive={activeFeature === 'woocommerce'}
                        />
                        <MobileVisualContainer>
                            <MobileVisualWrapper>
                                <WooCommerceVisual />
                            </MobileVisualWrapper>
                            <MobileVisualLabel>
                                <MobileVisualIconSmall featureId="woocommerce">
                                    <ShoppingCart size={18} />
                                </MobileVisualIconSmall>
                                Real-time order status and product info
                            </MobileVisualLabel>
                        </MobileVisualContainer>

                    </TextColumn>

                    <StickyColumn>
                        <DashboardShell activeFeature={activeFeature} />
                    </StickyColumn>
                </SplitLayout>

                <CtaSection />
                <ComprehensiveFAQ />
                <Footer />
            </PageContainer>
        </ThemeProvider>
    );
};

export default FeaturesPage;
